import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { paymentService } from "../../services/paymentService";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  BanknotesIcon,
  CalendarIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";

const PaymentReceipt = () => {
  const { saleId } = useParams();
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchReceipt();
  }, [saleId]);

  const fetchReceipt = async () => {
    try {
      const response = await paymentService.getReceipt(saleId);
      setReceiptData(response.data || response);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load receipt";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "processing":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Link
          to="/sales"
          className="inline-flex items-center text-sm text-green-600 hover:text-green-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Sale History
        </Link>
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error === "Not authorized" ? "Not Authorized" : "Receipt Not Found"}
          </h2>
          <p className="text-gray-600 mb-6">
            {error === "Not authorized"
              ? "You do not have permission to view this receipt."
              : "The sale receipt could not be found. It may have been removed."}
          </p>
          <Link
            to="/sales"
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Go to Sale History
          </Link>
        </div>
      </div>
    );
  }

  const { sale, payment, bank, user } = receiptData || {};
  const saleAmount = sale?.totalPrice || 0;
  const paidDate = payment?.paidAt ? new Date(payment.paidAt).toLocaleDateString() : "—";

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back Button */}
      <Link
        to="/sales"
        className="inline-flex items-center text-sm text-green-600 hover:text-green-700"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        Back to Sale History
      </Link>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <DocumentTextIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Payment Receipt
              </h1>
              <p className="text-gray-600">
                Sale #{sale?._id?.slice(-10) || "N/A"}
              </p>
            </div>
          </div>
          <span
            className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(
              payment?.status || sale?.status
            )}`}
          >
            {(payment?.status || sale?.status || "Unknown").charAt(0).toUpperCase() +
              (payment?.status || sale?.status || "Unknown").slice(1)}
          </span>
        </div>
      </div>

      {/* Payment Details */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          <BanknotesIcon className="h-5 w-5 inline mr-2 text-gray-500" />
          Payment Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
              Payment Reference
            </label>
            <p className="text-sm text-gray-900 mt-1 font-mono">
              {payment?.paymentReference || "—"}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
              Transfer Code
            </label>
            <p className="text-sm text-gray-900 mt-1 font-mono">
              {payment?.transferCode || "—"}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount Paid
            </label>
            <p className="text-lg font-bold text-green-600 mt-1">
              ₦{saleAmount.toLocaleString()}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
              <CalendarIcon className="h-3 w-3 inline mr-1" />
              Payment Date
            </label>
            <p className="text-sm text-gray-900 mt-1">
              {paidDate}
            </p>
          </div>
        </div>
        {payment?.receipt && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-3">
            <div className="flex items-center text-sm text-green-800">
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Payment confirmed — ₦{(payment.receipt.amount / 100).toLocaleString()} received successfully
            </div>
          </div>
        )}
      </div>

      {/* Bank Details */}
      {bank && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <IdentificationIcon className="h-5 w-5 inline mr-2 text-gray-500" />
            Bank Account
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bank Name
              </label>
              <p className="text-sm text-gray-900 mt-1">{bank.bankName || "—"}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account Number
              </label>
              <p className="text-sm text-gray-900 mt-1">{bank.accountNumber || "—"}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                Account Name
              </label>
              <p className="text-sm text-gray-900 mt-1">{bank.accountName || "—"}</p>
            </div>
          </div>
        </div>
      )}

      {/* Sale Details */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Sale Details</h2>
        <div className="space-y-2">
          {sale?.materials?.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-2 bg-gray-50 rounded"
            >
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-900">
                  {item.material?.name || "Unknown Material"}
                </span>
                <span className="text-sm text-gray-600">
                  ({item.quantity}kg)
                </span>
              </div>
              <span className="text-sm text-gray-900 font-medium">
                ₦{(item.subtotal || 0).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
          <span className="text-base font-medium text-gray-900">Total</span>
          <span className="text-xl font-bold text-green-600">
            ₦{saleAmount.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between items-center pt-2 text-sm text-gray-600">
          <span>Total Quantity</span>
          <span>{sale?.totalQuantity || 0}kg</span>
        </div>
      </div>

      {/* User Info */}
      {user && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Paid To</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </label>
              <p className="text-sm text-gray-900 mt-1">
                {user.firstName || ""} {user.lastName || ""}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </label>
              <p className="text-sm text-gray-900 mt-1">{user.email || "—"}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReceipt;
